import type { WorkflowDefinition } from "../types";

/**
 * W4 — New-business prospecting.
 *
 * The only playbook that never touches a property. Prospects are added, scanned
 * and pitched entirely from the organization dashboard's Prospects tab, so every
 * step routes to `org:/?tab=prospects` and `bindsProperty` is deliberately
 * false: if the prospect signs, the property already exists with its report
 * history intact and W1 picks it up.
 *
 * `shared`: the prospect list is the organization's, not one person's. Anyone on
 * the team continues the same checklist, because the state it observes —
 * prospect count, completed reports, exports — is organization state.
 *
 * Live since PR 6, which built the organization-level library and runner — a
 * playbook whose every step lives on an org page had nowhere to run until then.
 */
export const prospectingWorkflow: WorkflowDefinition = {
	id: "prospecting",
	wNumber: 4,
	title: "Prospect new clients with AI visibility reports",
	outcome:
		"Turn a list of target brands into branded reports that open the pitch conversation.",
	audiences: ["agencies"],
	uses: ["Prospects", "AI Reports", "White-label branding"],
	scope: "organization",
	bindsProperty: false,
	concurrency: "shared",
	progress: "observed",
	// Bulk prospecting needs an active Agency plan; the same predicate the
	// prospects API enforces, so the playbook is never offered to an
	// organization that would be refused at the first step.
	isApplicable: (_mode, org) => org.canProspect,
	articleSlug: "how-to-prospect-new-clients-with-ai-visibility-reports",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "enable-prospecting",
			title: "Open the Prospects tab",
			page: "Prospects",
			route: "org:/?tab=prospects",
			criterion: "Prospecting is available on your plan.",
			why: "Prospects live in their own list, so target accounts never mix into the brands you already track. It comes with an Agency plan.",
			docsHref: "/docs/dashboards/prospecting",
			tasks: [
				"Switch the organization dashboard to the Prospects tab",
				"Check your plan covers prospecting, because trials do not",
			],
			complete: { kind: "orgFlag", key: "canProspect" },
			signal: ({ state }) => ({
				kind: "checklist",
				items: [
					{
						label: "Prospecting available on this plan",
						done: state.organization.canProspect,
					},
					{
						label: "Your branding ready for shared reports",
						done: state.organization.hasWhiteLabelBranding,
					},
				],
			}),
		},
		{
			id: "bulk-add-prospects",
			title: "Add your prospect list in bulk",
			page: "Prospects",
			route: "org:/?tab=prospects",
			criterion: "You've added at least one prospect.",
			why: "Scanning several brands on the same day gives you a fair comparison. Every row is checked first, so a typo never costs you a report.",
			docsHref: "/docs/dashboards/prospecting",
			tasks: [
				"Paste the domains or upload a CSV",
				"Fix the rows flagged invalid and drop brands you already track",
				"Keep the batch to the next few weeks of calls",
			],
			complete: { kind: "orgCount", key: "prospectCount", gte: 1 },
			signal: ({ state }) => ({
				kind: "count",
				label: "Prospects added",
				value: state.organization.prospectCount,
				target: 1,
			}),
		},
		{
			id: "run-prospect-reports",
			title: "Start the reports and read each one",
			page: "Prospects",
			route: "org:/?tab=prospects",
			criterion: "You've completed at least one prospect report.",
			why: "A report tests a brand across the AI platforms from its domain alone. Then read each one for a single finding you can lead with.",
			docsHref: "/docs/dashboards/ai-visibility-reports",
			tasks: [
				"Start the batch and wait for the completion email",
				"Read each report looking for one finding, not five",
				"Lead with a named rival or a low consistency score",
			],
			complete: {
				kind: "orgCount",
				key: "prospectReportsCompleteCount",
				gte: 1,
			},
			spend: { kind: "prospectReports" },
			signal: ({ state }) => ({
				kind: "count",
				label: "Prospect reports completed",
				value: state.organization.prospectReportsCompleteCount,
				target: Math.max(1, state.organization.prospectCount),
			}),
		},
		{
			id: "share-report",
			title: "Send the branded report before the call",
			page: "Prospects",
			route: "org:/?tab=prospects",
			criterion: "You've exported or shared a prospect report.",
			why: "The PDF, slides and link all carry your own branding. A prospect who has read it arrives with a question, not a blank stare.",
			docsHref: "/docs/dashboards/ai-visibility-reports",
			tasks: [
				"Export the PDF or slides, or copy the share link",
				"Send it ahead of the call with your one finding",
			],
			complete: {
				kind: "orgCount",
				key: "prospectReportsExportedCount",
				gte: 1,
			},
			signal: ({ state }) => ({
				kind: "checklist",
				items: [
					{
						label: `${state.organization.prospectReportsExportedCount} reports exported or shared`,
						done:
							state.organization.prospectReportsExportedCount > 0,
					},
					{
						label: "Your branding on the shared report",
						done: state.organization.hasWhiteLabelBranding,
					},
				],
			}),
		},
	],
};
